import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  InputAdornment,
  Menu,
  ListItemIcon,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { 
  Download, 
  Search,
  Business,
  TrendingUp,
  CheckCircle,
  Schedule,
  Person,
  Phone,
  Email,
  LocationOn,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getAssociates } from '../../services/associate';
import { leadService } from '../../services/lead';
import { getLeadReport } from '../../services/reports';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';

const LeadReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    source: '',
    assignedTo: '',
    searchTerm: ''
  });

  // Associate dropdown states with pagination
  const [associateOptions, setAssociateOptions] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateFetchingRef = useRef(false);
  const associateInitialLoadRef = useRef(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [associateHasMore, setAssociateHasMore] = useState(true);
  const associateSearchTermRef = useRef('');

  // Lead dropdown states with pagination
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadSearchInput, setLeadSearchInput] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(false);
  const leadFetchingRef = useRef(false);
  const leadInitialLoadRef = useRef(false);
  const [leadPage, setLeadPage] = useState(1);
  const [leadHasMore, setLeadHasMore] = useState(true);
  const leadSearchTermRef = useRef('');

  // Lead report data states
  const [leadReportData, setLeadReportData] = useState([]);
  const [loadingLeadReport, setLoadingLeadReport] = useState(false);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);

  const sampleData = [
    {
      sno: 1,
      leadId: 'LD001',
      leadDate: '2025-01-10',
      customerName: 'Rahul Verma',
      phone: '+91-9876543210',
      email: 'rahul.verma@email.com',
      source: 'Website',
      status: 'Hot',
      assignedTo: 'Rajesh Kumar',
      followupDate: '2025-01-15',
      location: 'Mumbai',
      priority: 'High'
    },
    {
      sno: 2,
      leadId: 'LD002',
      leadDate: '2025-01-11',
      customerName: 'Priya Patel',
      phone: '+91-9123456780',
      email: 'priya.patel@email.com',
      source: 'Reference',
      status: 'Warm',
      assignedTo: 'Priya Sharma',
      followupDate: '2025-01-16',
      location: 'Delhi',
      priority: 'Medium'
    },
    {
      sno: 3,
      leadId: 'LD003',
      leadDate: '2025-01-12',
      customerName: 'Suresh Kumar',
      phone: '+91-9988776655',
      email: 'suresh.kumar@email.com',
      source: 'Walk-in',
      status: 'Cold',
      assignedTo: 'Vikram Singh',
      followupDate: '2025-01-20',
      location: 'Bangalore',
      priority: 'Low'
    },
    {
      sno: 4,
      leadId: 'LD004',
      leadDate: '2025-01-13',
      customerName: 'Anita Desai',
      phone: '+91-9876543211',
      email: 'anita.desai@email.com',
      source: 'Social Media',
      status: 'Hot',
      assignedTo: 'Amit Sharma',
      followupDate: '2025-01-18',
      location: 'Pune',
      priority: 'High'
    },
    {
      sno: 5,
      leadId: 'LD005',
      leadDate: '2025-01-14',
      customerName: 'Vikram Mehta',
      phone: '+91-9123456781',
      email: 'vikram.mehta@email.com',
      source: 'Advertisement',
      status: 'Warm',
      assignedTo: 'Neha Kumar',
      followupDate: '2025-01-22',
      location: 'Chennai',
      priority: 'Medium'
    }
  ];

  const statusOptions = ['All', 'Hot', 'Warm', 'Cold'];
  const [sourceOptions, setSourceOptions] = useState(['All']);
  const [loadingSources, setLoadingSources] = useState(false);
  const assignedToOptions = ['All', 'Rajesh Kumar', 'Priya Sharma', 'Vikram Singh', 'Amit Sharma', 'Neha Kumar'];

  // Fetch sources from API
  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${baseURL}/api/globalMasterDetails?type=source`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch sources: ${response.statusText}`);
        }
        
        const responseData = await response.json();
        const sources = Array.isArray(responseData.data)
          ? responseData.data
              .filter(item => item.status === true && !item.isDeleted)
              .map(item => item.details?.sourceName || 'Unknown')
              .filter(Boolean)
          : [];
        
        // Add 'All' option at the beginning
        setSourceOptions(['All', ...sources]);
      } catch (error) {
        console.error('Error fetching sources:', error);
        // Keep default 'All' option on error
        setSourceOptions(['All']);
      } finally {
        setLoadingSources(false);
      }
    };

    fetchSources();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleReset = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      status: 'All',
      source: 'All',
      assignedTo: 'All',
      searchTerm: ''
    });
    setSelectedAssociate(null);
    setSelectedLead(null);
    setAssociateSearchInput('');
    setLeadSearchInput('');
    setPage(0);
    // Trigger fresh fetch after resetting
    // But since filters is used in fetchLeadReportData dependency, it might trigger if we call it or if we rely on useEffect
    // However, the component only has useEffect(..., []) so we should call it manually if we want fresh data on reset
    fetchLeadReportData();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch associates for autocomplete with pagination
  const fetchAssociates = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (associateFetchingRef.current) return;
    if (!append && !associateHasMore && pageNum > 1) return;
    
    associateFetchingRef.current = true;
    setLoadingAssociates(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await getAssociates(params);
      
      let associates = [];
      let total = 0;
      
      // Extract data and total from response
      if (response.status && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (response.success && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (Array.isArray(response.data?.docs)) {
        associates = response.data.docs;
        total = parseInt(response.extra?.total || response.data.totalDocs || response.totalCount || associates.length, 10);
      }

      const formattedAssociates = associates.map((associate) => ({
        id: associate._id || associate.id,
        name: associate.name || '',
        number: String(associate.associateId || associate._id || ''),
        phone: String(associate.phone || associate.whatsapp || ''),
      }));

      if (append) {
        setAssociateOptions(prev => {
          const newOptions = [...prev, ...formattedAssociates];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
          setAssociateHasMore(hasMore);
          return newOptions;
        });
      } else {
        setAssociateOptions(formattedAssociates);
        const currentTotal = formattedAssociates.length;
        const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
        setAssociateHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociateOptions([]);
        setAssociateHasMore(false);
      }
    } finally {
      setLoadingAssociates(false);
      associateFetchingRef.current = false;
    }
  }, []);

  // Debounced associate search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = associateSearchInput.trim();
    
    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      if (!associateInitialLoadRef.current) {
        associateInitialLoadRef.current = true;
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (associateSearchTermRef.current !== trimmedInput) {
      setAssociatePage(1);
      setAssociateHasMore(true);
      associateSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchAssociates(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [associateSearchInput, fetchAssociates]);

  // Fetch leads for autocomplete with pagination
  const fetchLeads = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (leadFetchingRef.current) return;
    if (!append && !leadHasMore && pageNum > 1) return;
    
    leadFetchingRef.current = true;
    setLoadingLeads(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await leadService.getLeads(params);
      
      let leads = [];
      let total = 0;
      
      // Extract data from response - response is already json.data from leadService
      if (response && Array.isArray(response.docs)) {
        leads = response.docs;
        total = parseInt(response.totalDocs || response.total || leads.length, 10);
      } else if (Array.isArray(response)) {
        leads = response;
        total = leads.length;
      }

      const formattedLeads = leads.map((lead) => ({
        id: lead._id || lead.id,
        leadId: lead.leadId || '',
        name: lead.customerDetails?.name || lead.name || '',
        phone: String(lead.customerDetails?.phone || lead.phone || ''),
        email: lead.customerDetails?.email || lead.email || '',
        source: lead.source || '',
        status: lead.status || '',
        associateName: lead.associateDetails?.name || '',
        _raw: lead, // Store raw data
      }));

      if (append) {
        setLeadOptions(prev => {
          const newOptions = [...prev, ...formattedLeads];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedLeads.length >= params.limit;
          setLeadHasMore(hasMore);
          return newOptions;
        });
      } else {
        setLeadOptions(formattedLeads);
        const currentTotal = formattedLeads.length;
        const hasMore = currentTotal < total && formattedLeads.length >= params.limit;
        setLeadHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (!append) {
        setLeadOptions([]);
        setLeadHasMore(false);
      }
    } finally {
      setLoadingLeads(false);
      leadFetchingRef.current = false;
    }
  }, []);

  // Debounced lead search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = leadSearchInput.trim();
    
    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      if (!leadInitialLoadRef.current) {
        leadInitialLoadRef.current = true;
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (leadSearchTermRef.current !== trimmedInput) {
      setLeadPage(1);
      setLeadHasMore(true);
      leadSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchLeads(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [leadSearchInput, fetchLeads]);

  // Fetch lead report data
  const fetchLeadReportData = useCallback(async () => {
    setLoadingLeadReport(true);
    try {
      const params = {};

      // Add filter parameters
      if (filters.fromDate) {
        params.fromDate = filters.fromDate;
      }
      if (filters.toDate) {
        params.toDate = filters.toDate;
      }
      if (filters.status && filters.status !== 'All') {
        params.status = filters.status;
      }
      if (filters.source && filters.source !== 'All') {
        params.source = filters.source;
      }
      if (selectedAssociate && selectedAssociate.id) {
        params.associateId = selectedAssociate.id;
      }
      if (selectedLead && selectedLead.leadId) {
        params.leadId = selectedLead.leadId;
      }

      const response = await getLeadReport(params);

      if (response.success && Array.isArray(response.data)) {
        // Map API response to table format
        const mappedData = response.data.map((item, index) => ({
          sno: index + 1,
          leadId: `LD${item.leadId || ''}`,
          leadDate: item.leadDate ? new Date(item.leadDate).toISOString().split('T')[0] : '',
          customerName: item.customerName || '',
          phone: item.contact?.phone || '',
          email: item.contact?.email || '',
          source: item.source || '',
          status: item.status || '',
          assignedTo: item.assignedTo || '',
          followupDate: item.followUpDate ? new Date(item.followUpDate).toISOString().split('T')[0] : '',
          _raw: item, // Store raw data
        }));

        setLeadReportData(mappedData);
        setTotalLeadsCount(response.extra?.total || mappedData.length);
      } else {
        setLeadReportData([]);
        setTotalLeadsCount(0);
      }
    } catch (error) {
      console.error('Error fetching lead report:', error);
      setLeadReportData([]);
      setTotalLeadsCount(0);
    } finally {
      setLoadingLeadReport(false);
    }
  }, [filters.fromDate, filters.toDate, filters.status, filters.source, selectedAssociate, selectedLead]);

  // Fetch data on initial load only
  useEffect(() => {
    fetchLeadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter data based on search term (client-side filtering for search term only)
  const getFilteredData = () => {
    if (!filters.searchTerm) {
      return leadReportData;
    }
    
    const searchTerm = filters.searchTerm.toLowerCase();
    return leadReportData.filter(item => 
      item.customerName.toLowerCase().includes(searchTerm) ||
      item.leadId.toLowerCase().includes(searchTerm) ||
      item.phone.includes(searchTerm) ||
      item.email.toLowerCase().includes(searchTerm)
    );
  };

  const filteredData = getFilteredData();

  // Export functionality
  const buildExportRows = () => {
    return filteredData.map((lead, index) => ({
      'S.No': index + 1,
      'Lead ID': lead.leadId,
      'Lead Date': lead.leadDate,
      'Customer Name': lead.customerName,
      'Phone': lead.phone,
      'Email': lead.email,
      'Source': lead.source,
      'Status': lead.status,
      'Assigned To': lead.assignedTo,
      'Followup Date': lead.followupDate,
    }));
  };

  const getExportFileName = () => `Lead_Report_${new Date().toISOString().split('T')[0]}`;

  const handleExportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsCSV({ rows, fileNameBase: getExportFileName() });
  };

  const handleExportPdf = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsPDF({
      rows,
      title: 'Lead Report',
      fileNameBase: getExportFileName(),
      orientation: 'l' // Landscape for wide tables
    });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsExcel({
      rows,
      sheetName: 'Leads',
      fileNameBase: getExportFileName()
    });
  };

  const hotLeads = filteredData.filter(item => item.status === 'Hot').length;
  const warmLeads = filteredData.filter(item => item.status === 'Warm').length;
  const coldLeads = filteredData.filter(item => item.status === 'Cold').length;
  const totalLeads = filteredData.length;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Lead Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive lead management and tracking report
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
            />
            <RefreshButton
              onClick={() => fetchLeadReportData()}
              loading={loadingLeadReport}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <Business sx={{ fontSize: 40, color: '#536DFE', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {totalLeads}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Leads
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <TrendingUp sx={{ fontSize: 40, color: '#f44336', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {hotLeads}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Hot Leads
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircle sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {warmLeads}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Warm Leads
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <Schedule sx={{ fontSize: 40, color: '#2196f3', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {coldLeads}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Cold Leads
            </Typography>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Lead Report Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select 
                  value={filters.status} 
                  onChange={(e) => handleFilterChange('status', e.target.value)} 
                  label="Status"
                >
                  {statusOptions.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select 
                  value={filters.source} 
                  onChange={(e) => handleFilterChange('source', e.target.value)} 
                  label="Source"
                >
                  {sourceOptions.map(source => (
                    <MenuItem key={source} value={source}>{source}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={associateOptions}
                getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setAssociateSearchInput(newInputValue);
                    if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                      setAssociatePage(1);
                      setAssociateHasMore(true);
                      associateSearchTermRef.current = '';
                    }
                  }
                }}
                onOpen={() => {
                  if (associateOptions.length === 0 && !associateFetchingRef.current) {
                    setAssociatePage(1);
                    setAssociateHasMore(true);
                    fetchAssociates(associateSearchInput || '', 1, false);
                  }
                }}
                loading={loadingAssociates}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;
                    
                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (associateHasMore && !loadingAssociates && !associateFetchingRef.current) {
                        const nextPage = associatePage + 1;
                        setAssociatePage(nextPage);
                        fetchAssociates(associateSearchInput || '', nextPage, true);
                      }
                    }
                  },
                  style: { maxHeight: '300px', overflow: 'auto' },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    String(option.number || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Associate"
                    placeholder="Type name or number..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.number} {option.phone ? `• ${option.phone}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No associates found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={leadOptions}
                getOptionLabel={(option) => option.name ? `LD${option.leadId} - ${option.name}` : ''}
                value={selectedLead}
                onChange={(event, newValue) => {
                  setSelectedLead(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setLeadSearchInput(newInputValue);
                    if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                      setLeadPage(1);
                      setLeadHasMore(true);
                      leadSearchTermRef.current = '';
                    }
                  }
                }}
                onOpen={() => {
                  if (leadOptions.length === 0 && !leadFetchingRef.current) {
                    setLeadPage(1);
                    setLeadHasMore(true);
                    fetchLeads(leadSearchInput || '', 1, false);
                  }
                }}
                loading={loadingLeads}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;
                    
                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (leadHasMore && !loadingLeads && !leadFetchingRef.current) {
                        const nextPage = leadPage + 1;
                        setLeadPage(nextPage);
                        fetchLeads(leadSearchInput || '', nextPage, true);
                      }
                    }
                  },
                  style: { maxHeight: '300px', overflow: 'auto' },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    String(option.leadId || '').toLowerCase().includes(searchTerm) ||
                    String(option.phone || '').includes(searchTerm) ||
                    (option.email || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Lead"
                    placeholder="Type name, ID, phone..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingLeads ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        LD{option.leadId} - {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.phone} {option.email ? `• ${option.email}` : ''}
                      </Typography>
                      {option.source && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Source: {option.source} {option.status ? `• Status: ${option.status}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText="No leads found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchLeadReportData}
                  disabled={loadingLeadReport}
                  startIcon={loadingLeadReport ? <CircularProgress size={16} color="inherit" /> : <Search />}
                  sx={{
                    backgroundColor: '#536DFE',
                    '&:hover': { backgroundColor: '#3F51B5' },
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '40px',
                    borderRadius: 1.5
                  }}
                >
                  {loadingLeadReport ? 'Searching...' : 'Search'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '40px',
                    borderRadius: 1.5
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
          

        </CardContent>
      </Card>

      {/* Data Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Lead Report Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Lead ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Lead Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Contact</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Source</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Assigned To</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Followup Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingLeadReport ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No lead data found for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                    <TableRow
                      key={row.leadId || row._raw?._id || index}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.leadId}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.leadDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.customerName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone sx={{ fontSize: 14, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {row.phone}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Email sx={{ fontSize: 14, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {row.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.source} 
                          size="small" 
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={row.status} 
                          size="small" 
                          color={row.status === 'Hot' ? 'error' : row.status === 'Warm' ? 'warning' : 'info'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.assignedTo}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.followupDate}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                backgroundColor: '#f8f9fa',
              }
            }}
          />
        </CardContent>
      </Card>


    </Box>
  );
};

export default LeadReport;

